import { useCallback, useEffect, useState } from 'react';

async function apiRequest(apiBase, path, options = {}) {
  const response = await fetch(`${apiBase}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.ok) {
    throw new Error(payload.message || 'Request failed');
  }
  return payload;
}

const countdownFieldDefinitions = [
  { key: 'label', label: 'Label / Kicker' },
  { key: 'title', label: 'Judul' },
  { key: 'date_text', label: 'Teks Tanggal' },
  { key: 'description', label: 'Deskripsi', multiline: true },
  { key: 'target_date', label: 'Target Date' },
  { key: 'complete_message', label: 'Pesan Selesai' },
  { key: 'days_label', label: 'Label Days' },
  { key: 'hours_label', label: 'Label Hours' },
  { key: 'minutes_label', label: 'Label Minutes' },
  { key: 'seconds_label', label: 'Label Seconds' },
];

function createEmptyCountdownLanguage() {
  return {
    label: '',
    title: '',
    date_text: '',
    description: '',
    target_date: '',
    complete_message: '',
    days_label: '',
    hours_label: '',
    minutes_label: '',
    seconds_label: '',
  };
}

function parseMaybeJson(value) {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }

  if (typeof value === 'object') {
    return value;
  }

  return null;
}

function normalizeCountdownLanguageContent(source = {}) {
  const targetDateValue = source.target_date || source.targetDate || source.countdown_date || source.countdownDate || '';
  const dateOnlyValue = targetDateValue ? String(targetDateValue).slice(0, 10) : '';

  return {
    label: source.label || source.countdown_label || source.countdownLabel || '',
    title: source.title || source.countdown_title || source.countdownTitle || '',
    date_text: source.date_text || source.dateText || source.countdown_date_text || source.countdownDateText || '',
    description: source.description || source.countdown_description || source.countdownDescription || '',
    target_date: dateOnlyValue,
    complete_message: source.complete_message || source.completeMessage || source.countdown_complete_message || source.countdownCompleteMessage || '',
    days_label: source.days_label || source.daysLabel || '',
    hours_label: source.hours_label || source.hoursLabel || '',
    minutes_label: source.minutes_label || source.minutesLabel || '',
    seconds_label: source.seconds_label || source.secondsLabel || '',
  };
}

function formatReadableCountdownDate(dateValue, language) {
  if (!dateValue) {
    return '';
  }

  const parsed = new Date(`${dateValue}T07:00:00+07:00`);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return parsed.toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function toIsoCountdownDate(dateValue) {
  if (!dateValue) {
    return '';
  }

  return `${dateValue}T07:00:00+07:00`;
}

function normalizeCountdownSectionContent(content) {
  const parsed = parseMaybeJson(content) || {};
  const sourceEn = parsed.en || parsed;
  const sourceId = parsed.id || parsed.en || parsed;

  return {
    en: normalizeCountdownLanguageContent(sourceEn),
    id: normalizeCountdownLanguageContent(sourceId),
  };
}

function CmsWorkflowPanel({ apiBase }) {
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sectionLoading, setSectionLoading] = useState(true);
  const [sectionError, setSectionError] = useState('');
  const [notice, setNotice] = useState('');

  const [countdownSection, setCountdownSection] = useState(null);
  const [countdownForm, setCountdownForm] = useState({
    section_id: '',
    change_reason: '',
    en: createEmptyCountdownLanguage(),
    id: createEmptyCountdownLanguage(),
  });

  const [newRequest, setNewRequest] = useState({
    content_id: '',
    change_summary: '',
    proposed_body: '',
    principal_reviewer_id: '',
    final_reviewer_id: '',
  });

  const loadPendingApprovals = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiRequest(apiBase, '/homepage/cms/pending-approvals');
      setPendingApprovals(res.data?.items || []);
    } catch (err) {
      setError(err.message || 'Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  const loadCountdownSection = useCallback(async () => {
    setSectionLoading(true);
    setSectionError('');
    try {
      const res = await apiRequest(apiBase, '/homepage/cms/sections');
      const sections = Array.isArray(res.data) ? res.data : [];
      const section = sections.find((item) => item.section_type === 'countdown') || null;
      setCountdownSection(section);

      const normalized = normalizeCountdownSectionContent(section?.content_published || section?.content);
      setCountdownForm({
        section_id: section?.id ? String(section.id) : '',
        change_reason: '',
        en: normalized.en,
        id: normalized.id,
      });
    } catch (err) {
      setSectionError(err.message || 'Failed to load countdown section');
      setCountdownSection(null);
      setCountdownForm({
        section_id: '',
        change_reason: '',
        en: createEmptyCountdownLanguage(),
        id: createEmptyCountdownLanguage(),
      });
    } finally {
      setSectionLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadPendingApprovals();
  }, [loadPendingApprovals]);

  useEffect(() => {
    loadCountdownSection();
  }, [loadCountdownSection]);

  async function handleSubmitRequest(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await apiRequest(apiBase, '/cms/workflow/submit', {
        method: 'POST',
        body: JSON.stringify({
          content_id: Number(newRequest.content_id),
          change_summary: newRequest.change_summary,
          proposed_body: newRequest.proposed_body,
          principal_reviewer_id: Number(newRequest.principal_reviewer_id),
          final_reviewer_id: Number(newRequest.final_reviewer_id),
        }),
      });
      setNewRequest({
        content_id: '',
        change_summary: '',
        proposed_body: '',
        principal_reviewer_id: '',
        final_reviewer_id: '',
      });
      await loadPendingApprovals();
    } catch (err) {
      setError(err.message || 'Failed to submit change request');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCountdownSubmit(event) {
    event.preventDefault();
    setSubmitting(true);
    setSectionError('');
    setNotice('');

    try {
      const sectionId = Number(countdownForm.section_id);
      if (!sectionId) {
        throw new Error('Countdown section not found');
      }

      const normalizedContent = {
        en: {
          ...countdownForm.en,
          target_date: toIsoCountdownDate(countdownForm.en.target_date),
          date_text: countdownForm.en.date_text || formatReadableCountdownDate(countdownForm.en.target_date, 'en'),
        },
        id: {
          ...countdownForm.id,
          target_date: toIsoCountdownDate(countdownForm.id.target_date),
          date_text: countdownForm.id.date_text || formatReadableCountdownDate(countdownForm.id.target_date, 'id'),
        },
      };

      const payloadContent = normalizedContent;

      await apiRequest(apiBase, `/homepage/cms/sections/${sectionId}/submit-approval`, {
        method: 'POST',
        body: JSON.stringify({
          content: payloadContent,
          fieldName: null,
          oldValue: countdownSection?.content_published || countdownSection?.content || null,
          newValue: payloadContent,
          fieldType: 'json',
          oldData: countdownSection?.content_published || countdownSection?.content || null,
          newData: payloadContent,
          comment: countdownForm.change_reason,
          reason: countdownForm.change_reason,
        }),
      });

      setNotice('Perubahan countdown berhasil dikirim ke approver. Homepage publik akan berubah setelah disetujui.');
      await Promise.all([loadCountdownSection(), loadPendingApprovals()]);
    } catch (err) {
      setSectionError(err.message || 'Failed to submit countdown for approval');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDecision(requestId, decision, rejectionReason = '') {
    setSubmitting(true);
    setError('');
    try {
      const endpoint = decision === 'approve'
        ? `/homepage/cms/pending-approvals/${requestId}/approve`
        : `/homepage/cms/pending-approvals/${requestId}/reject`;

      await apiRequest(apiBase, endpoint, {
        method: 'POST',
        body: JSON.stringify({ reason: rejectionReason || (decision === 'approve' ? 'Approved' : 'Need revision') }),
      });
      await loadPendingApprovals();
    } catch (err) {
      setError(err.message || 'Failed to process review');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="super-admin-card">
      <h3>Countdown CMS Editor</h3>
      <p className="super-admin-subtitle">
        Ubah field countdown di homepage. Perubahan masuk antrian approval dan publik baru berubah setelah disetujui.
      </p>

      {sectionError && <div className="alert alert-error">{sectionError}</div>}
      {notice && <div className="alert alert-info">{notice}</div>}

      {sectionLoading ? (
        <div className="alert alert-info">Loading countdown section...</div>
      ) : countdownSection ? (
        <form className="super-admin-create-form" onSubmit={handleCountdownSubmit}>
          <div className="alert alert-info">
            Section ID: {countdownSection.id} | Status: {countdownSection.status}
          </div>

          <label>
            Reason for change
            <input
              type="text"
              value={countdownForm.change_reason}
              onChange={(event) => setCountdownForm((prev) => ({ ...prev, change_reason: event.target.value }))}
              placeholder="Contoh: Update jadwal opening day"
            />
          </label>

          {(['en', 'id']).map((language) => (
            <fieldset key={language}>
              <legend>{language === 'en' ? 'English' : 'Bahasa Indonesia'}</legend>
              {countdownFieldDefinitions.map((field) => (
                <label key={`${language}-${field.key}`}>
                  {field.label}
                  {field.multiline ? (
                    <textarea
                      className="super-admin-textarea"
                      value={countdownForm[language][field.key]}
                      onChange={(event) => setCountdownForm((prev) => ({
                        ...prev,
                        [language]: {
                          ...prev[language],
                          [field.key]: event.target.value,
                        },
                      }))}
                    />
                  ) : (
                    field.key === 'target_date' ? (
                      <input
                        type="date"
                        value={countdownForm[language][field.key]}
                        onChange={(event) => setCountdownForm((prev) => {
                          const nextDate = event.target.value;
                          return {
                            ...prev,
                            [language]: {
                              ...prev[language],
                              target_date: nextDate,
                              date_text: formatReadableCountdownDate(nextDate, language),
                            },
                          };
                        })}
                      />
                    ) : (
                      <input
                        type="text"
                        value={countdownForm[language][field.key]}
                        onChange={(event) => setCountdownForm((prev) => ({
                          ...prev,
                          [language]: {
                            ...prev[language],
                            [field.key]: event.target.value,
                          },
                        }))}
                      />
                    )
                  )}
                </label>
              ))}
            </fieldset>
          ))}

          <button type="submit" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Countdown for Approval'}
          </button>
        </form>
      ) : (
        <div className="alert alert-error">Countdown section not found.</div>
      )}

      <h3 style={{ marginTop: '2rem' }}>CMS Workflow Queue (3-Level Approval)</h3>
      {error && <div className="alert alert-error">{error}</div>}
      {loading && <div className="alert alert-info">Loading CMS workflow...</div>}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Section</th>
              <th>Type</th>
              <th>Submitted By</th>
              <th>Submitted At</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {pendingApprovals.map((item) => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>{item.section_name || item.section_type || '-'}</td>
                <td>{item.change_type || item.action_type || '-'}</td>
                <td>{item.full_name || item.submitted_by || '-'}</td>
                <td>{item.created_at || item.submitted_at || '-'}</td>
                <td>{item.status || item.approval_status || 'pending'}</td>
                <td>
                  <button type="button" onClick={() => handleDecision(item.id, 'approve')} disabled={submitting}>Approve</button>
                  <button type="button" onClick={() => handleDecision(item.id, 'reject', 'Need revision')} disabled={submitting}>Reject</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default CmsWorkflowPanel;
